import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { Item } from '../types';
import ItemForm from './ItemForm';
import './AddItem.css';

const AddItem: React.FC = () => {
  const { user } = useAuth();
  const { addItem } = useData();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleAddItem = async (partialItem: Partial<Item>) => {
    if (!user) return;

    setIsSubmitting(true);
    
    try {
      // Validate location fields before creating the item
      if (!partialItem.location?.address || !partialItem.location?.city || !partialItem.location?.postcode) {
        alert('Please fill in all location fields (address, city, and postcode)');
        setIsSubmitting(false);
        return;
      }

      // Create a complete item from the partial item
      const newItem: Item = {
        id: Math.random().toString(36).substr(2, 9), // Generate a random ID
        name: partialItem.name || '',
        description: partialItem.description || '',
        dailyRateGBP: partialItem.dailyRateGBP || 10, // Default to £10 if not specified
        images: partialItem.images || [],
        ownerId: user.id,
        minDays: partialItem.minDays || 1,
        maxDays: partialItem.maxDays || 7, // Default to 7 days if not specified
        location: partialItem.location || {
          address: '',
          city: '',
          postcode: '',
          coordinates: { latitude: 0, longitude: 0 }
        },
        category: partialItem.category || '',
        availability: partialItem.availability ?? true,
        rentalPeriods: partialItem.rentalPeriods || []
      };

      // Save the item to the data source (this will geocode the address)
      const success = await addItem(newItem);
      
      if (success) {
        console.log('Item added successfully');
        // Navigate back to My Items page
        navigate('/my-items');
      } else {
        console.error('Failed to add item');
        alert('Failed to add item. Please check that the address is valid and try again.');
      }
    } catch (error) {
      console.error('Error adding item:', error);
      alert('Error adding item. Please check that the address is valid and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/my-items');
  };

  if (!user) {
    return <div>Please log in to add items.</div>;
  }

  return (
    <div className="add-item-page">
      <div className="add-item-header">
        <h1>Add New Item</h1>
        <button 
          className="back-button"
          onClick={handleCancel}
        >
          ← Back to My Items
        </button>
      </div>
      
      <div className="add-item-content">
        <ItemForm 
          onSubmit={handleAddItem}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
        />
      </div>
    </div>
  );
};

export default AddItem; 